import { useEffect, useState, useMemo } from "react";
import api from "../../utils/api";

export default function Withdraw() {
  const [settings, setSettings] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentGateway, setPaymentGateway] = useState("UPI");
  const [paymentDetails, setPaymentDetails] = useState("");

  const [bankDetails, setBankDetails] = useState({
    accountNumber: "",
    ifsc: "",
    beneficiaryName: "",
    bankName: "",
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userId = user?._id;

  const availableBalance = Number(user?.totalEarningAmount || 0);

  /* ================= SAFE CURRENCY HANDLING ================= */
  const currency = useMemo(() => {
    if (!settings?.currency) return "?";

    if (typeof settings.currency === "object") {
      return settings.currency.symbol || "?";
    }

    return settings.currency;
  }, [settings]);

  /* ================= Load Settings ================= */
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadSettings = async () => {
      try {
        const res = await api.get(
          "/client/withdrawalRequest/fetchSettingDetails",
          { params: { userId } }
        );

        if (res?.data?.status) {
          setSettings(res.data.data);
        }
      } catch (err) {
        console.error("Settings Load Error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [userId]);

  /* ================= Submit ================= */
  const handleSubmit = async () => {
    const numericAmount = Number(amount);

    if (!numericAmount || numericAmount <= 0) {
      alert("Please enter valid withdrawal amount.");
      return;
    }

    if (numericAmount > availableBalance) {
      alert("Insufficient balance.");
      return;
    }

    if (
      settings &&
      numericAmount < Number(settings.minWithdrawalRequestedAmount)
    ) {
      alert(
        `Minimum withdrawal amount is ${currency} ${settings.minWithdrawalRequestedAmount}`
      );
      return;
    }

    let detailsArray = [];

    if (paymentGateway === "Bank") {
      const { accountNumber, ifsc, beneficiaryName, bankName } = bankDetails;

      if (!accountNumber || !ifsc || !beneficiaryName || !bankName) {
        alert("Please fill all bank details.");
        return;
      }

      detailsArray = [
        `Account Number: ${accountNumber}`,
        `IFSC: ${ifsc.toUpperCase()}`,
        `Beneficiary: ${beneficiaryName}`,
        `Bank Name: ${bankName}`,
      ];
    } else {
      if (!paymentDetails.trim()) {
        alert("Please enter payment details.");
        return;
      }

      detailsArray = [paymentDetails.trim()];
    }

    try {
      setSubmitting(true);

      const res = await api.post(
        "/client/withdrawalRequest/createWithdrawRequest",
        {
          userId,
          requestAmount: numericAmount,
          paymentGateway,
          paymentDetails: detailsArray,
        }
      );

      alert(res?.data?.message || "Withdrawal request submitted.");

      if (res?.data?.status) {
        setAmount("");
        setPaymentDetails("");
        setBankDetails({
          accountNumber: "",
          ifsc: "",
          beneficiaryName: "",
          bankName: "",
        });
      }
    } catch (err) {
      console.error(err);
      alert("Failed to submit withdrawal.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>Loading withdrawal settings...</p>;

  return (
    <div className="panel-container withdraw-container">
      <h2>Withdraw Earnings</h2>

      {/* ===== Top Grid ===== */}
      <div className="withdraw-top-grid">
        <div className="withdraw-balance-card">
          <h3>Available Balance</h3>
          <p>
            {currency} {availableBalance.toFixed(2)}
          </p>
        </div>

        <div className="withdraw-min-card">
          <h3>Minimum Withdrawal</h3>
          <p>
            {currency} {settings?.minWithdrawalRequestedAmount || 0}
          </p>
        </div>
      </div>

      {/* ===== Form ===== */}
      <div className="withdraw-form-card">
        <h3>Request Withdrawal</h3>

        <input
          type="number"
          placeholder={`Enter withdrawal amount (Min ${currency} ${settings?.minWithdrawalRequestedAmount || 0})`}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="withdraw-input"
        />

        <select
          value={paymentGateway}
          onChange={(e) => setPaymentGateway(e.target.value)}
          className="withdraw-input"
        >
          <option value="UPI">UPI</option>
          <option value="Bank">Bank Transfer</option>
          <option value="PayPal">PayPal</option>
        </select>

        {paymentGateway === "Bank" ? (
          <>
            <input
              type="text"
              placeholder="Account Number"
              value={bankDetails.accountNumber}
              onChange={(e) =>
                setBankDetails({
                  ...bankDetails,
                  accountNumber: e.target.value.replace(/\D/g, ""),
                })
              }
              className="withdraw-input"
            />

            <input
              type="text"
              placeholder="IFSC Code"
              value={bankDetails.ifsc}
              onChange={(e) =>
                setBankDetails({
                  ...bankDetails,
                  ifsc: e.target.value.toUpperCase(),
                })
              }
              className="withdraw-input"
            />

            <input
              type="text"
              placeholder="Beneficiary Name"
              value={bankDetails.beneficiaryName}
              onChange={(e) =>
                setBankDetails({
                  ...bankDetails,
                  beneficiaryName: e.target.value,
                })
              }
              className="withdraw-input"
            />

            <input
              type="text"
              placeholder="Bank Name"
              value={bankDetails.bankName}
              onChange={(e) =>
                setBankDetails({
                  ...bankDetails,
                  bankName: e.target.value,
                })
              }
              className="withdraw-input"
            />
          </>
        ) : (
          <input
            type="text"
            placeholder={
              paymentGateway === "UPI"
                ? "Enter UPI ID"
                : "Enter PayPal Email"
            }
            value={paymentDetails}
            onChange={(e) => setPaymentDetails(e.target.value)}
            className="withdraw-input"
          />
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="withdraw-btn"
        >
          {submitting ? "Submitting..." : "Submit Withdrawal Request"}
        </button>
      </div>
    </div>
  );
}
